import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  Search,
  Send,
  Sparkles,
  Newspaper,
  UploadCloud,
  BarChart3,
  LayoutGrid,
  Loader2,
  BadgeCheck,
  User as UserIcon,
  Bot,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { INDUSTRIES, COMPANY_SIZES, industryLabel } from "@/data/taxonomy";
import CASES from "@/data/caseStudies";
import SEED_NEWS from "@/data/news.js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readMember = () => {
  try {
    return JSON.parse(localStorage.getItem("deployd_member") || "null");
  } catch {
    return null;
  }
};

const genSessionId = (email) => {
  const existing = localStorage.getItem("deployd_chat_session");
  if (existing) return existing;
  const sid = `${(email || "anon").split("@")[0]}-${Date.now().toString(36)}`;
  localStorage.setItem("deployd_chat_session", sid);
  return sid;
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [tab, setTab] = useState("feed");

  useEffect(() => {
    const m = readMember();
    if (!m) navigate("/", { replace: true });
    else setMember(m);
  }, [navigate]);

  if (!member) return null;

  return (
    <main
      data-testid="dashboard-page"
      className="relative min-h-screen w-full bg-[#F7F6F1] text-zinc-900"
    >
      <div className="grain-overlay" />

      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 bg-white/70 px-6 py-4 backdrop-blur sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 text-sm font-medium text-zinc-600 transition-colors hover:text-emerald-700"
        >
          <BrandMark size="inline" />
          <span className="text-zinc-300">/</span>
          <span>workspace</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-zinc-600 sm:inline">
            Signed in as{" "}
            <span className="font-semibold text-zinc-900">
              {member.full_name || member.email}
            </span>
            {member.industry && (
              <>
                {" · "}
                <span className="text-emerald-700">{industryLabel(member.industry)}</span>
              </>
            )}
          </span>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:border-emerald-500 hover:text-emerald-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Home
          </Link>
        </div>
      </header>

      <section className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-16 pt-10 sm:px-10 sm:pt-14 lg:px-16">
        <p className="text-sm font-medium tracking-tight text-emerald-700">
          Hello, {member.full_name?.split(" ")[0] || member.email}
        </p>
        <h1 className="mt-2 font-display text-4xl font-semibold leading-[0.98] tracking-tight text-zinc-900 sm:text-5xl">
          Your workspace.
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
          Discover verified use cases, contribute what you know, and see what your
          peers are researching.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList
            data-testid="dashboard-tabs"
            className="grid w-full grid-cols-3 rounded-2xl border border-zinc-200 bg-white p-1 sm:w-fit sm:grid-cols-3"
          >
            <TabsTrigger
              value="feed"
              data-testid="tab-feed"
              className="gap-2 rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
            >
              <LayoutGrid className="h-4 w-4" />
              Feed
            </TabsTrigger>
            <TabsTrigger
              value="contribute"
              data-testid="tab-contribute"
              className="gap-2 rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
            >
              <UploadCloud className="h-4 w-4" />
              Contribute
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              data-testid="tab-analytics"
              className="gap-2 rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="feed" className="mt-8 focus-visible:outline-none">
            <FeedTab member={member} />
          </TabsContent>

          <TabsContent value="contribute" className="mt-8 focus-visible:outline-none">
            <ContributeTab member={member} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-8 focus-visible:outline-none">
            <AnalyticsTab member={member} />
          </TabsContent>
        </Tabs>
      </section>
    </main>
  );
}

// ─── Tab 1 — Feed + Search + Chat ─────────────────────────────────────────
function FeedTab({ member }) {
  const [query, setQuery] = useState("");
  const [remoteNews, setRemoteNews] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);
  const sessionId = useMemo(() => genSessionId(member.email), [member.email]);

  useEffect(() => {
    axios
      .get(`${API}/news`, { params: { industry: member.industry, limit: 20 } })
      .then((r) => setRemoteNews(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRemoteNews([]));
  }, [member.industry]);

  // Debounced search-log to /api/search-log
  useEffect(() => {
    if (!query.trim()) return;
    const t = setTimeout(() => {
      axios
        .post(`${API}/search-log`, {
          email: member.email,
          industry: member.industry,
          query: query.trim(),
        })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [query, member.email, member.industry]);

  const filteredCases = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = [...CASES].sort((a, b) => {
      const am = a.industryKey === member.industry ? -1 : 0;
      const bm = b.industryKey === member.industry ? -1 : 0;
      return am - bm;
    });
    if (!q) return base;
    return base.filter((c) =>
      [c.vendor, c.customer, c.industry, c.summary, c.problem, c.solution, c.metric]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [query, member.industry]);

  const mergedNews = useMemo(() => {
    const combined = [...remoteNews, ...SEED_NEWS];
    const seen = new Set();
    const unique = combined.filter((n) => {
      const k = n.id || n.title;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    unique.sort((a, b) => {
      const am = a.industry === member.industry ? -1 : 0;
      const bm = b.industry === member.industry ? -1 : 0;
      return am - bm;
    });
    const q = query.trim().toLowerCase();
    if (!q) return unique;
    return unique.filter((n) =>
      [n.title, n.blurb, n.source, n.industry].join(" ").toLowerCase().includes(q)
    );
  }, [remoteNews, query, member.industry]);

  const sendChat = async (e) => {
    e?.preventDefault();
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChatMessages((m) => [...m, { role: "user", content: msg }]);
    setChatInput("");
    setChatBusy(true);
    try {
      const { data } = await axios.post(`${API}/agent/chat`, {
        session_id: sessionId,
        message: msg,
        email: member.email,
        industry: member.industry,
      });
      setChatMessages((m) => [...m, { role: "assistant", content: data.reply || "" }]);
    } catch (err) {
      setChatMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I hit an error. Please try again." },
      ]);
    } finally {
      setChatBusy(false);
      requestAnimationFrame(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }
  };

  return (
    <div className="space-y-10">
      {/* Search bar */}
      <div
        data-testid="feed-search-wrap"
        className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-[0_1px_0_0_rgba(10,10,10,0.02),0_20px_40px_-24px_rgba(10,10,10,0.12)] focus-within:border-emerald-500"
      >
        <Search className="h-5 w-5 text-zinc-400" />
        <input
          data-testid="feed-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search verified cases, news, vendors, outcomes…"
          className="flex-1 border-0 bg-transparent text-base text-zinc-900 placeholder-zinc-400 outline-none sm:text-lg"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="text-xs font-medium text-zinc-500 hover:text-emerald-700"
          >
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left: cases + news */}
        <div className="space-y-10 lg:col-span-2">
          {/* Cases */}
          <section data-testid="feed-cases">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-900">
                Verified use cases
                {member.industry && (
                  <span className="ml-2 text-base font-medium text-zinc-500">
                    · {industryLabel(member.industry)} pinned
                  </span>
                )}
              </h2>
              <span className="text-xs font-medium text-zinc-500">
                {filteredCases.length} of {CASES.length}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {filteredCases.slice(0, 12).map((c) => (
                <Link
                  key={c.id}
                  to={`/case-study/${c.id}`}
                  data-testid={`feed-case-${c.id}`}
                  className={`group flex h-full flex-col gap-2 rounded-2xl border bg-white p-5 no-underline transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_1px_0_0_rgba(10,10,10,0.02),0_18px_36px_-24px_rgba(10,10,10,0.12)] ${
                    c.industryKey === member.industry
                      ? "border-emerald-500"
                      : "border-zinc-200"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-600">
                      {c.industry}
                    </span>
                    {c.industryKey === member.industry && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
                        <BadgeCheck className="h-3 w-3" />
                        Your industry
                      </span>
                    )}
                  </div>
                  <p className="font-display text-lg font-semibold tracking-tight text-zinc-900 group-hover:text-emerald-700">
                    {c.customer}{" "}
                    <span className="text-sm font-medium text-zinc-500">
                      · {c.vendor}
                    </span>
                  </p>
                  <p className="font-display text-2xl font-bold tracking-tight text-emerald-700">
                    {c.metric}{" "}
                    <span className="text-xs font-medium text-zinc-500">
                      {c.metricLabel}
                    </span>
                  </p>
                  <p className="text-[13.5px] leading-relaxed text-zinc-600">
                    {c.summary}
                  </p>
                </Link>
              ))}
            </div>
            {filteredCases.length === 0 && (
              <div className="mt-4 rounded-2xl border border-dashed border-zinc-200 bg-white/60 p-8 text-center text-sm text-zinc-500">
                No cases match your search. Ask the concierge on the right →
              </div>
            )}
          </section>

          {/* News */}
          <section data-testid="feed-news">
            <div className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-emerald-600" />
              <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-900">
                Industry news
              </h2>
            </div>
            <div className="mt-4 space-y-3">
              {mergedNews.slice(0, 10).map((n) => (
                <a
                  key={n.id || n.title}
                  href={n.url || "#"}
                  target={n.url ? "_blank" : "_self"}
                  rel="noreferrer"
                  data-testid={`feed-news-${n.id || "item"}`}
                  className="group flex items-start justify-between gap-4 rounded-2xl border border-zinc-200 bg-white p-4 no-underline transition-colors hover:border-emerald-300"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-zinc-500">
                      <span>{n.source || "News"}</span>
                      {n.industry && (
                        <>
                          <span className="text-zinc-300">·</span>
                          <span>{industryLabel(n.industry)}</span>
                        </>
                      )}
                      {n.industry === member.industry && (
                        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          <BadgeCheck className="h-3 w-3" />
                          Your industry
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-display text-base font-semibold leading-snug text-zinc-900 group-hover:text-emerald-700 sm:text-lg">
                      {n.title}
                    </p>
                    {n.blurb && (
                      <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                        {n.blurb}
                      </p>
                    )}
                  </div>
                  {n.url && (
                    <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-zinc-400 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-600" />
                  )}
                </a>
              ))}
              {mergedNews.length === 0 && (
                <div className="rounded-2xl border border-dashed border-zinc-200 bg-white/60 p-6 text-center text-sm text-zinc-500">
                  Nothing yet. Try the Contribute tab to submit a news item.
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Right: chat agent */}
        <aside
          data-testid="agent-chat-panel"
          className="flex h-fit flex-col gap-4 rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_0_0_rgba(10,10,10,0.02),0_20px_40px_-24px_rgba(10,10,10,0.12)] lg:sticky lg:top-6"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">
              deployd Concierge
            </h3>
          </div>
          <p className="text-xs font-medium text-zinc-500">
            Ask about verified cases in your industry. Powered by GPT.
          </p>

          <div
            data-testid="chat-messages"
            className="flex max-h-[520px] min-h-[240px] flex-col gap-3 overflow-y-auto rounded-xl bg-zinc-50 p-3"
          >
            {chatMessages.length === 0 && (
              <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-4 text-sm text-zinc-500">
                Try: <em>&quot;Which vendors solve AR reconciliation for
                D2C?&quot;</em> or <em>&quot;Show me FinaHQ&apos;s fastest close
                results&quot;</em>.
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <Bot className="h-3.5 w-3.5" />
                  </span>
                )}
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-800 shadow-[0_1px_0_0_rgba(10,10,10,0.04)]"
                  }`}
                >
                  {m.content}
                </div>
                {m.role === "user" && (
                  <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700">
                    <UserIcon className="h-3.5 w-3.5" />
                  </span>
                )}
              </div>
            ))}
            {chatBusy && (
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Concierge is thinking…
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={sendChat} className="flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask the Concierge…"
              className="flex-1 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 outline-none focus:border-emerald-500"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={chatBusy || !chatInput.trim()}
              className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-3 py-2.5 text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </aside>
      </div>
    </div>
  );
}

// ─── Tab 2 — Contribute (case + news uploads) ─────────────────────────────
function ContributeTab({ member }) {
  const emptyCase = {
    vendor: "",
    customer: "",
    industry: "",
    size: "",
    roi: "",
    problem: "",
    solution: "",
  };
  const emptyNews = { title: "", url: "", source: "", industry: "", blurb: "" };
  const [caseForm, setCaseForm] = useState(emptyCase);
  const [newsForm, setNewsForm] = useState(emptyNews);
  const [busyCase, setBusyCase] = useState(false);
  const [busyNews, setBusyNews] = useState(false);

  const submitCase = async (e) => {
    e.preventDefault();
    if (!caseForm.vendor.trim() || !caseForm.customer.trim()) {
      toast.error("Vendor and customer are required.");
      return;
    }
    setBusyCase(true);
    try {
      await axios.post(`${API}/user-case-upload`, { ...caseForm, email: member.email });
      toast.success("Case submitted for review.");
      setCaseForm(emptyCase);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit.");
    } finally {
      setBusyCase(false);
    }
  };

  const submitNews = async (e) => {
    e.preventDefault();
    if (!newsForm.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setBusyNews(true);
    try {
      await axios.post(`${API}/news-upload`, {
        ...newsForm,
        email: member.email,
        industry: newsForm.industry || member.industry || "",
      });
      toast.success("News item submitted for review.");
      setNewsForm(emptyNews);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit.");
    } finally {
      setBusyNews(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Case submission */}
      <div
        data-testid="contribute-case"
        className="rounded-2xl border border-zinc-200 bg-white p-6"
      >
        <div className="flex items-center gap-2 border-b border-zinc-100 pb-4">
          <UploadCloud className="h-5 w-5 text-emerald-600" />
          <h3 className="font-display text-xl font-semibold tracking-tight text-zinc-900">
            Submit a use case
          </h3>
        </div>
        <p className="mt-3 text-xs font-medium text-zinc-500">
          Verified with the customer before publication.
        </p>
        <form onSubmit={submitCase} className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField
            testId="up-vendor"
            label="Vendor"
            value={caseForm.vendor}
            onChange={(e) => setCaseForm({ ...caseForm, vendor: e.target.value })}
            placeholder="e.g. Zenskar"
          />
          <TextField
            testId="up-customer"
            label="Customer"
            value={caseForm.customer}
            onChange={(e) => setCaseForm({ ...caseForm, customer: e.target.value })}
            placeholder="e.g. 100ms"
          />
          <SelectField
            testId="up-industry"
            label="Industry"
            value={caseForm.industry}
            onChange={(e) => setCaseForm({ ...caseForm, industry: e.target.value })}
            options={INDUSTRIES}
          />
          <SelectField
            testId="up-size"
            label="Customer size"
            value={caseForm.size}
            onChange={(e) => setCaseForm({ ...caseForm, size: e.target.value })}
            options={COMPANY_SIZES}
          />
          <TextArea
            testId="up-problem"
            label="Business problem"
            value={caseForm.problem}
            onChange={(e) => setCaseForm({ ...caseForm, problem: e.target.value })}
            placeholder="What was breaking?"
            className="sm:col-span-2"
          />
          <TextArea
            testId="up-solution"
            label="What was deployed"
            value={caseForm.solution}
            onChange={(e) => setCaseForm({ ...caseForm, solution: e.target.value })}
            placeholder="What did the vendor ship?"
            className="sm:col-span-2"
          />
          <TextArea
            testId="up-roi"
            label="ROI / outcome"
            value={caseForm.roi}
            onChange={(e) => setCaseForm({ ...caseForm, roi: e.target.value })}
            placeholder="e.g. -40% cycle time, +$14M savings"
            className="sm:col-span-2"
          />
          <button
            type="submit"
            data-testid="up-submit"
            disabled={busyCase}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-emerald-600 disabled:opacity-60 sm:col-span-2"
          >
            {busyCase ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
            <span>{busyCase ? "Submitting…" : "Submit for verification"}</span>
          </button>
        </form>
      </div>

      {/* News submission */}
      <div
        data-testid="contribute-news"
        className="rounded-2xl border border-zinc-200 bg-white p-6"
      >
        <div className="flex items-center gap-2 border-b border-zinc-100 pb-4">
          <Newspaper className="h-5 w-5 text-emerald-600" />
          <h3 className="font-display text-xl font-semibold tracking-tight text-zinc-900">
            Share news for circulation
          </h3>
        </div>
        <p className="mt-3 text-xs font-medium text-zinc-500">
          Funding rounds, launches, deep-dives — anything relevant to CFOs.
        </p>
        <form onSubmit={submitNews} className="mt-5 grid grid-cols-1 gap-3">
          <TextField
            testId="news-title"
            label="Headline"
            value={newsForm.title}
            onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
            placeholder="e.g. Bluecopa raises Series A"
          />
          <TextField
            testId="news-url"
            label="Source URL"
            value={newsForm.url}
            onChange={(e) => setNewsForm({ ...newsForm, url: e.target.value })}
            placeholder="https://…"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              testId="news-source"
              label="Publication"
              value={newsForm.source}
              onChange={(e) => setNewsForm({ ...newsForm, source: e.target.value })}
              placeholder="e.g. The Economic Times"
            />
            <SelectField
              testId="news-industry"
              label="Industry"
              value={newsForm.industry}
              onChange={(e) => setNewsForm({ ...newsForm, industry: e.target.value })}
              options={INDUSTRIES}
            />
          </div>
          <TextArea
            testId="news-blurb"
            label="Short blurb"
            value={newsForm.blurb}
            onChange={(e) => setNewsForm({ ...newsForm, blurb: e.target.value })}
            placeholder="One or two lines on why this matters."
          />
          <button
            type="submit"
            data-testid="news-submit"
            disabled={busyNews}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-emerald-600 disabled:opacity-60"
          >
            {busyNews ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
            <span>{busyNews ? "Submitting…" : "Submit for review"}</span>
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Tab 3 — Analytics ─────────────────────────────────────────────────────
function AnalyticsTab({ member }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/me`, {
        params: { email: member.email, industry: member.industry },
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [member.email, member.industry]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your analytics…
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-zinc-500">No analytics available yet.</div>;
  }

  const industryName = member.industry ? industryLabel(member.industry) : "your industry";

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <StatCard
        testId="stat-peers"
        title={`Peers in ${industryName}`}
        value={data.peers_in_industry ?? 0}
        blurb="CFOs from your industry signed up."
      />
      <StatCard
        testId="stat-total-members"
        title="Total members"
        value={data.total_members ?? 0}
        blurb="CFOs across every industry on deployd."
      />

      <div
        data-testid="my-history"
        className="rounded-2xl border border-zinc-200 bg-white p-5 lg:col-span-1"
      >
        <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">
          Your recent searches
        </h3>
        <p className="mt-1 text-xs font-medium text-zinc-500">
          Last 10 · saved to your account.
        </p>
        <ul className="mt-4 space-y-2">
          {(data.my_recent || []).slice(0, 10).map((q, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-2 rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-800"
            >
              <span>{q.query}</span>
              <span className="text-[11px] text-zinc-500">
                {new Date(q.at).toLocaleDateString()}
              </span>
            </li>
          ))}
          {(!data.my_recent || data.my_recent.length === 0) && (
            <li className="rounded-xl border border-dashed border-zinc-200 bg-white p-4 text-center text-xs text-zinc-500">
              Start searching in the Feed tab — your history lands here.
            </li>
          )}
        </ul>
      </div>

      <div
        data-testid="peer-benchmark"
        className="rounded-2xl border border-zinc-200 bg-white p-5"
      >
        <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">
          What peers in {industryName} are searching
        </h3>
        <p className="mt-1 text-xs font-medium text-zinc-500">
          Top 10 queries · anonymised across your industry.
        </p>
        <ul className="mt-4 space-y-2">
          {(data.peer_top_queries || []).map((q, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-2 rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-800"
            >
              <span>{q.query}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                {q.count}×
              </span>
            </li>
          ))}
          {(!data.peer_top_queries || data.peer_top_queries.length === 0) && (
            <li className="rounded-xl border border-dashed border-zinc-200 bg-white p-4 text-center text-xs text-zinc-500">
              Not enough peer searches yet. Check back soon.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

const StatCard = ({ title, value, blurb, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-zinc-200 bg-white p-6">
    <p className="text-xs font-medium tracking-tight text-emerald-700">{title}</p>
    <p className="mt-2 font-display text-4xl font-semibold tracking-tight text-zinc-900">
      {value}
    </p>
    <p className="mt-2 text-sm text-zinc-600">{blurb}</p>
  </div>
);

const TextField = ({ testId, label, className = "", ...rest }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-xs font-medium text-zinc-500">{label}</label>
    <input
      data-testid={testId}
      {...rest}
      className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
    />
  </div>
);

const SelectField = ({ testId, label, options, ...rest }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-zinc-500">{label}</label>
    <select
      data-testid={testId}
      {...rest}
      className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-emerald-500"
    >
      <option value="">Select</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const TextArea = ({ testId, label, className = "", rows = 3, ...rest }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-xs font-medium text-zinc-500">{label}</label>
    <textarea
      data-testid={testId}
      rows={rows}
      {...rest}
      className="w-full resize-none rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
    />
  </div>
);
